import React, { useCallback, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faTriangleExclamation, faCheck, faTrash } from "@fortawesome/free-solid-svg-icons";
import { validateCoupon } from "../utils/couponCsv";

// The columns worth showing in the table. The rest stay on the row and are
// saved, but a fifteen-column grid is unreadable and these are what an editor
// actually checks before publishing.
const COLUMNS = [
  { key: "offerId", label: "Offer ID", width: "w-40" },
  { key: "store", label: "Store", width: "w-44" },
  { key: "title", label: "Title", width: "w-64" },
  { key: "couponCode", label: "Code", width: "w-32" },
  { key: "type", label: "Type", width: "w-24" },
  { key: "startDate", label: "Start", width: "w-28" },
  { key: "endDate", label: "End", width: "w-28" },
];

/**
 * Reviews a parsed CSV before any of it is stored.
 *
 * The previous flow uploaded the file to storage and let a worker decide what
 * to do with it, which meant the first sight of a bad row was a coupon missing
 * from the site days later. Here every row is visible and editable, and rows
 * that cannot be saved say why.
 */
const CsvReviewModal = ({
  fileName,
  rows,
  headerProblems,
  knownStores,
  onClose,
  onSaveRow,
  onSaveAll,
  busy,
}) => {
  const [coupons, setCoupons] = useState(rows);
  const [savedRows, setSavedRows] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [onlyProblems, setOnlyProblems] = useState(false);

  const update = (rowId, field, value) => {
    setCoupons((current) =>
      current.map((c) => {
        if (c.__row !== rowId) return c;
        return { ...c, [field]: value };
      })
    );
    // The row's own problems are recomputed on render; this only drops the
    // server's verdict, which was about the values before this edit.
    setRowErrors((current) => ({ ...current, [rowId]: undefined }));
  };

  // Drops a row from this import. Nothing has been written yet, so this only
  // takes it out of the list -- there is no coupon to delete.
  const removeRow = (rowId) => {
    setCoupons((current) => current.filter((c) => c.__row !== rowId));
    setRowErrors((current) => ({ ...current, [rowId]: undefined }));
  };

  const problemsFor = useCallback(
    (coupon) => {
      const live = validateCoupon(coupon, { knownStores, siblings: coupons });
      const fromServer = rowErrors[coupon.__row] ? [rowErrors[coupon.__row]] : [];
      return [...new Set([...live, ...fromServer])];
    },
    [coupons, knownStores, rowErrors]
  );

  const visible = useMemo(
    () => (onlyProblems ? coupons.filter((c) => problemsFor(c).length) : coupons),
    [coupons, onlyProblems, problemsFor]
  );

  const savableCount = coupons.filter(
    (c) => !savedRows[c.__row] && problemsFor(c).length === 0
  ).length;
  const savedCount = Object.values(savedRows).filter(Boolean).length;
  const problemCount = coupons.filter((c) => problemsFor(c).length > 0).length;

  const strip = (coupon) => {
    const { __row, __excelMangled, ...rest } = coupon;
    return rest;
  };

  const handleSaveRow = async (coupon) => {
    try {
      await onSaveRow(strip(coupon));
      setSavedRows((current) => ({ ...current, [coupon.__row]: true }));
      setRowErrors((current) => ({ ...current, [coupon.__row]: undefined }));
    } catch (error) {
      setRowErrors((current) => ({
        ...current,
        [coupon.__row]: error?.message || "Could not be saved",
      }));
    }
  };

  const handleSaveAll = async () => {
    const ready = coupons.filter((c) => !savedRows[c.__row] && problemsFor(c).length === 0);
    if (!ready.length) return;
    const outcome = await onSaveAll(ready.map(strip));
    const nextSaved = { ...savedRows };
    const nextErrors = { ...rowErrors };
    (outcome?.results || []).forEach((result, i) => {
      const rowId = ready[i].__row;
      if (result.saved) nextSaved[rowId] = true;
      else nextErrors[rowId] = result.error || "Could not be saved";
    });
    setSavedRows(nextSaved);
    setRowErrors(nextErrors);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-semibold">Review coupons</h2>
            <p className="text-sm text-gray-500 mt-1">
              {fileName} — {coupons.length} row{coupons.length === 1 ? "" : "s"}.
              Nothing is saved until you choose to save it.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl px-2">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {headerProblems?.length > 0 && (
          <div className="mx-6 mt-4 border border-amber-300 bg-amber-50 rounded p-3 text-sm text-amber-900">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
            {headerProblems.join(" · ")}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b bg-gray-50 text-sm">
          <span className="text-green-700 font-medium">{savedCount} saved</span>
          <span className="text-gray-500">·</span>
          <span>{savableCount} ready</span>
          <span className="text-gray-500">·</span>
          <span className={problemCount ? "text-red-600 font-medium" : "text-gray-500"}>
            {problemCount} need attention
          </span>
          <label className="ml-auto flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyProblems}
              onChange={(e) => setOnlyProblems(e.target.checked)}
            />
            Show only rows needing attention
          </label>
        </div>

        <div className="flex-1 overflow-auto px-6">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white shadow-sm">
              <tr>
                <th className="py-2 px-2 text-left w-12">#</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className={`py-2 px-2 text-left ${c.width}`}>
                    {c.label}
                  </th>
                ))}
                <th className="py-2 px-2 text-left w-32">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((coupon) => {
                const problems = problemsFor(coupon);
                const saved = savedRows[coupon.__row];
                return (
                  <React.Fragment key={coupon.__row}>
                    <tr className={saved ? "bg-green-50" : problems.length ? "bg-red-50" : ""}>
                      <td className="py-1 px-2 text-gray-400">{coupon.__row}</td>
                      {COLUMNS.map((c) => (
                        <td key={c.key} className="py-1 px-1">
                          <input
                            value={coupon[c.key] || ""}
                            disabled={saved || busy}
                            onChange={(e) => update(coupon.__row, c.key, e.target.value)}
                            className="w-full border border-gray-200 rounded px-2 py-1 focus:border-red-400 focus:outline-none disabled:bg-transparent disabled:border-transparent"
                          />
                        </td>
                      ))}
                      <td className="py-1 px-2">
                        {saved ? (
                          <span className="text-green-700 text-xs font-medium">
                            <FontAwesomeIcon icon={faCheck} className="mr-1" />
                            Saved
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <button
                              disabled={problems.length > 0 || busy}
                              onClick={() => handleSaveRow(coupon)}
                              className="text-xs px-3 py-1 rounded bg-red-500 text-white disabled:bg-gray-300 hover:bg-red-600"
                            >
                              Save
                            </button>
                            <button
                              disabled={busy}
                              onClick={() => removeRow(coupon.__row)}
                              title="Remove this row from the import"
                              className="text-gray-400 hover:text-red-600 px-1 disabled:opacity-40"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                    {problems.length > 0 && !saved && (
                      <tr className="bg-red-50">
                        <td />
                        <td colSpan={COLUMNS.length + 1} className="pb-2 px-2 text-xs text-red-700">
                          {problems.join(" · ")}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <p className="text-sm text-gray-500">
            {problemCount > 0
              ? "Rows needing attention are skipped by Save all — fix them above or save them individually."
              : "Every row is ready to save."}
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-100">
              Close
            </button>
            <button
              onClick={handleSaveAll}
              disabled={savableCount === 0 || busy}
              className="px-5 py-2 rounded bg-red-500 text-white disabled:bg-gray-300 hover:bg-red-600"
            >
              {busy ? "Saving…" : `Save all (${savableCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvReviewModal;
